import { ApiError, ApiTimeoutError } from '../../api/client'

/**
 * The relay's stable error codes that the Portal words for itself — the same set, and the same
 * wording within a word or two, as Support's `UserMessages.ForCode`, so an operator and a
 * technician looking at the same failure read the same thing. The full list and what each means is
 * docs/API-Errors.md; a code is never renamed or reused, so switching on one is safe where switching
 * on the relay's sentence never was.
 */
export const RelayErrorCode = {
  InvalidRequest: 'invalid_request',
  Unauthenticated: 'unauthenticated',
  AccessDenied: 'access_denied',
  NotFound: 'not_found',
  DeviceTimeout: 'device_timeout',
  Conflict: 'conflict',
  PayloadTooLarge: 'payload_too_large',
  Unprocessable: 'unprocessable',
  RateLimited: 'rate_limited',
  InternalError: 'internal_error',
  NotImplemented: 'not_implemented',
  UpstreamFailed: 'upstream_failed',
  ServiceUnavailable: 'service_unavailable',
  GatewayTimeout: 'gateway_timeout',
  DeviceNotFound: 'device_not_found',
  DeviceOffline: 'device_offline',
  EnrollmentInactive: 'enrollment_inactive',
  AgentOutdated: 'agent_outdated',
  DeviceAuthFailed: 'device_auth_failed',
  RelayUnreachable: 'relay_unreachable',
  NameTaken: 'name_taken',
  InvalidCredentials: 'invalid_credentials',
  InvalidToken: 'invalid_token',
  NotConfigured: 'not_configured',
  FileOperationFailed: 'file_operation_failed',
  FileNotFound: 'file_not_found',
  AgentLogUnavailable: 'agent_log_unavailable',
  DeviceCommandFailed: 'device_command_failed',
  PolicyConflict: 'policy_conflict',
  InvalidRule: 'invalid_rule',
  RolloutRefused: 'rollout_refused',
} as const

/** The sentences, shared with the viewers' and sign-in's wording. */
export const Messages = {
  forbiddenDevice: 'You do not have permission to do this on this device.',
  forbidden: 'You do not have permission to do this.',
  offline: 'This device is offline. Try again once it is back online.',
  unknownDevice: 'The relay does not know this device, or this account cannot see it.',
  agentOutdated: "This device's agent is too old for this and is being updated. Try again in a few minutes.",
  notEnrolled:
    'This device has not been approved: it is awaiting enrollment approval, quarantined or rejected.',
  relayUnreachable: "The relay holding this device's connection could not be reached. Try again shortly.",
  deviceTimedOut: 'The device did not answer in time. It may be busy or on a slow link; try again.',
  wrongCredentials: 'Email or password is not recognised.',
  expired: 'Your session has expired — sign in again.',
  rateLimited: 'Too many attempts. Wait a few minutes, then try again.',
  nameTaken: 'That name is already in use.',
  relayFault: 'The relay ran into a problem of its own. Quote the reference to support.',
  relayBusy: 'The relay cannot do that just now. Try again shortly.',
  notConfigured: 'That is not set up on this relay.',
  notSupported: 'This relay does not support that.',
  tooLarge: 'That is larger than the relay accepts.',
  fileRefused: 'The device refused the file operation.',
  fileMissing: 'That file is not on the device.',
  agentLogUnavailable:
    "This device's agent does not offer its log: it logs to the console, or is too old to offer it.",
  commandFailed: 'The device reported that the command failed.',
  deviceAuthFailed: 'Authentication on the device failed.',
  relayTimedOut: 'The relay did not answer in time. Check the connection and try again.',
} as const

/**
 * The sentence for one of the relay's error codes; undefined for no code, or one with no wording of
 * its own, which the caller words by status instead.
 *
 * Where the code says only "the request was wrong, and the sentence says how" — a validation
 * failure, a conflict — and where it is the device's own answer — a refused file operation, a
 * failed printer command — the relay's sentence is the message. Everything else gets a fixed one,
 * because the relay's wording is free to change and these are what the operator acts on.
 */
export function messageForCode(
  code: string | undefined,
  detail: string | undefined,
  subject: 'device' | 'record' = 'device',
): string | undefined {
  switch (code) {
    case RelayErrorCode.DeviceOffline:
      return Messages.offline
    case RelayErrorCode.DeviceNotFound:
      return Messages.unknownDevice
    case RelayErrorCode.AccessDenied:
      return subject === 'device' ? Messages.forbiddenDevice : Messages.forbidden
    case RelayErrorCode.AgentOutdated:
      return Messages.agentOutdated
    case RelayErrorCode.EnrollmentInactive:
      return Messages.notEnrolled
    case RelayErrorCode.RelayUnreachable:
      return Messages.relayUnreachable
    case RelayErrorCode.DeviceTimeout:
    case RelayErrorCode.GatewayTimeout:
      return Messages.deviceTimedOut
    case RelayErrorCode.InvalidCredentials:
      return Messages.wrongCredentials
    case RelayErrorCode.Unauthenticated:
    case RelayErrorCode.InvalidToken:
      return Messages.expired
    case RelayErrorCode.RateLimited:
      return Messages.rateLimited
    case RelayErrorCode.NameTaken:
      return Messages.nameTaken
    case RelayErrorCode.InternalError:
      return Messages.relayFault
    case RelayErrorCode.ServiceUnavailable:
    case RelayErrorCode.UpstreamFailed:
      return Messages.relayBusy
    case RelayErrorCode.NotConfigured:
      return Messages.notConfigured
    case RelayErrorCode.NotImplemented:
      return Messages.notSupported
    case RelayErrorCode.PayloadTooLarge:
      return Messages.tooLarge
    case RelayErrorCode.DeviceAuthFailed:
      return Messages.deviceAuthFailed
    case RelayErrorCode.FileOperationFailed:
      return detail ?? Messages.fileRefused
    case RelayErrorCode.FileNotFound:
      return detail ?? Messages.fileMissing
    case RelayErrorCode.AgentLogUnavailable:
      return Messages.agentLogUnavailable
    case RelayErrorCode.DeviceCommandFailed:
      return detail ?? Messages.commandFailed
    case RelayErrorCode.InvalidRequest:
    case RelayErrorCode.Conflict:
    case RelayErrorCode.Unprocessable:
    case RelayErrorCode.InvalidRule:
    case RelayErrorCode.PolicyConflict:
    case RelayErrorCode.RolloutRefused:
    case RelayErrorCode.NotFound:
      return detail
    default:
      return undefined
  }
}

/**
 * "Reference: <id>" for a relay failure that has one, for a page that shows its own fixed sentence
 * and the reference beneath it; undefined for anything else.
 */
export function referenceOf(error: unknown): string | undefined {
  return error instanceof ApiError && error.requestId ? `Reference: ${error.requestId}` : undefined
}

/** The message with the request's id after it, for an operator to quote; as it is without one. */
export function withReference(message: string, requestId: string | undefined): string {
  return requestId ? `${message} (Reference: ${requestId})` : message
}


/**
 * Turns a failed call into something an operator can act on.
 *
 * This is the part of the port that is a deliberate improvement rather than a translation. The
 * Blazor `ApiClient` returns `null` or `false` for every non-2xx, so the pages could not tell a
 * permission failure from a missing device, a device that was offline, or a relay that timed out —
 * every one of them read as "Failed to …". Docker was worse still: `resp?.Success == false` is
 * false for a null response, so a refused container action produced no message at all and the list
 * simply refetched, which looks exactly like success.
 *
 * The relay is specific about all of this and always was: 403 naming the permission it wanted, 404
 * for a device that is not there, 409 for one that is offline or busy, 408 when it did not answer.
 * `ApiError` carries the status, so the page can say which.
 *
 * **The code comes first.** A relay that answers with problem details (docs/API-Errors.md) names
 * the reason in `code` — `device_offline` rather than "some 409" — and that is worded by
 * {@link messageForCode}. The status is the fallback for an older relay, or a code with no wording
 * of its own. Either way the request's id, when the relay confirmed one, follows as
 * "(Reference: …)": the one thing an operator can quote that finds the request in the relay's log.
 *
 * **The subject is a parameter, because this helper outgrew the pages it was written for.** It was
 * written for the Connect viewers, where every failure really is about a device, and its wording
 * says so — then 46 pages that have nothing to do with devices adopted it. A deep link to a deleted
 * job answered "The device was not found." directly above the job page's own explanation of what a
 * missing job means, and a rollout did the same. Passing `'device'` keeps the specific wording
 * where it is true; the default says only what is known, which is that the thing is not there.
 *
 * 408 and 409 are device-shaped by nature — a job does not time out and is not offline — so away
 * from a device they fall through to the fallback with the status attached rather than inventing a
 * cause.
 */
export function describeFailure(
  error: unknown,
  fallback: string,
  subject: 'device' | 'record' = 'record',
): string {
  // Before the ApiError check, since a timeout is one. It has no status to report, and the honest
  // reading is about the relay rather than the thing being asked for.
  if (error instanceof ApiTimeoutError) {
    return Messages.relayTimedOut
  }

  if (!(error instanceof ApiError)) {
    return fallback
  }

  return withReference(
    messageForCode(error.code, error.detail, subject) ?? byStatus(error.status, fallback, subject),
    error.requestId,
  )
}

function byStatus(status: number, fallback: string, subject: 'device' | 'record'): string {
  const device = subject === 'device'

  switch (status) {
    case 403:
      // The server names the exact permission in its body, but saying "file.upload required" to a
      // shop manager helps nobody — the actionable part is who to ask.
      return device ? Messages.forbiddenDevice : Messages.forbidden
    case 404:
      // Away from a device, 404 genuinely means "gone, or never yours": these endpoints filter by
      // access rather than refusing, so a record somebody else owns answers exactly as a deleted
      // one does. Saying both is the honest reading.
      return device
        ? 'The device was not found.'
        : 'It could not be found. It may have been deleted, or you may not have access to it.'
    case 408:
      return device
        ? 'The device did not answer in time. It may be busy or on a poor connection.'
        : `${fallback} (HTTP ${status})`
    case 409:
      // The relay uses 409 both for an offline device and for a conflicting operation; without a
      // code to say which, "offline" is overwhelmingly the common case on the device pages.
      return device
        ? 'The device is offline, so this could not be carried out.'
        : `${fallback} (HTTP ${status})`
    default:
      // The status is kept rather than swallowed: it is the one thing that makes an unexpected
      // failure diagnosable from a screenshot.
      return `${fallback} (HTTP ${status})`
  }
}
